import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { customersApi } from '@/features/customers/api'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { queryKeys } from '@/lib/query-keys'

interface ArchivedMatchHintProps {
  /** The customer search as typed. */
  search: string
  /** Switch to the Archived view (records managers only). Without it the hint
      tells staff to ask a manager instead. */
  onShowArchived?: () => void
  className?: string
}

/* Beside every customer search — the directory, check-in and the sale
   picker: "this person is archived, not missing". It used to show only in the
   directory, only to managers, and only when nothing else matched — so staff
   added the person again (refused: the email is taken) or picked the wrong
   Sarah. The count is all staff are told; restoring stays a manager's job. */
export function ArchivedMatchHint({ search, onShowArchived, className }: ArchivedMatchHintProps) {
  const term = useDebouncedValue(search.trim(), 300)
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.customers({ archivedCount: term }),
    queryFn: () => customersApi.customers.archivedCount(term),
    enabled: term.length >= 2,
  })
  const total = data?.total ?? 0
  // Only a fresh count is shown — never a remembered one while it re-checks,
  // so a customer restored, merged or erased elsewhere can't linger here.
  if (total === 0 || isFetching || term.length < 2) return null

  return (
    <p className={`text-sm text-muted-foreground ${className ?? ''}`} data-archived-hint>
      {total === 1 ? '1 archived customer matches.' : `${total} archived customers match.`}{' '}
      {onShowArchived ? (
        <Button variant="link" className="h-auto p-0 text-sm" onClick={onShowArchived}>
          View archived
        </Button>
      ) : (
        'Ask a manager to restore them — don’t add them again.'
      )}
    </p>
  )
}
